import { useState, useContext, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setUserRole } from "../store/Slices/auth/authoriseSlice";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Typography,
  Tooltip,
  Divider,
  useMediaQuery,
  Chip,
  Button, // Add Button
} from "@mui/material";

import {
  LightModeOutlined as LightModeOutlinedIcon,
  DarkModeOutlined as DarkModeOutlinedIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  WidgetsOutlined as WidgetsOutlinedIcon,
  MenuOutlined as MenuOutlinedIcon,
  CampaignOutlined as AnnouncementIcon,
  ConnectWithoutContact as ConnectIcon,
  HowToVote as VoteIcon,
  EmojiEventsOutlined as EmojiEventsOutlinedIcon,
} from "@mui/icons-material";

import InstituteProfileButton from "./InstituteProfileButton";
import InstitutionFilters from "./InstitutionFilters";
import { useTheme } from "@mui/material/styles";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import Profile from "../assets/imgs/profile.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ColorModeContext, tokens } from "../theme";
import { Icon } from "@iconify/react";
import { AuthService } from "../services/dataService";
import api from "../services/api";
import clearAllSlicesThunk from "../store/Slices/auth/logout";
import Searchbar from "./top/Searchbar";
import MembershipBadge from "./MembershipBadge";
import AnimatedModal from "./AnimatedModal";
import connectimg from "../assets/imgs/favicon (3).png";
import ConnectRegistrationBadge from "./ConnectRegistrationBadge";
import ConnectFeedbackBadge from "./ConnectFeedbackBadge";
// Ticket modal & Icon
import TicketModal from "../components/dashboard/InternshipSection/TicketModal";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CareerTalkBadge from "./top/CareerTalkBadge";
import AwardWinnerPopup from "../components/AwardWinnerPopup";
import VotingButton from "./VotingButton";

const HEADER_MOBILE = 48;
const HEADER_DESKTOP = 56;

const Topbar = ({ onToggleDrawer }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const isDarkMode = theme.palette.mode === "dark";
  const [notificationCount, setNotificationCount] = useState(0);
  const [roles, setRoles] = useState([]);
  const [isMultiRole, setIsMultiRole] = useState(false);
  const showIconButton = useMediaQuery(theme.breakpoints.down("lg"));
  const showMobileHeader = useMediaQuery(theme.breakpoints.down("sm"));
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingSwitch, setIsLoadingSwitch] = useState(false);
  const userRole = localStorage.getItem("Authorise");
  const dispatch = useDispatch();
  const location = useLocation();
  const isActiveRole = useSelector((state) => state.authorise.userRole);
  const userName = localStorage.getItem("userName");
  const formattedUserName = userName
    ? userName
        .split("@")[0] // Get the part before '@'
        .replace(/(?:^|\s|\.)(.)/g, (match, letter) => letter.toUpperCase()) // Capitalize the first letter after space or dot
        .replace(/([a-z])([A-Z])/g, "$1 $2") // Add a space between camelCase
    : "";

  const shouldShowInstitutionFilters = [
    "/total-staff",
    "/internship-all-staff",
    "/student-staff",
  ].includes(location.pathname);

  // State for modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState(null);
  const profileButtonRef = useRef(null);
  // Ticket Modal State
  const [isTicketModalOpen, setIsTicketModalOpen] = useState(false);
  const [showRibbon, setShowRibbon] = useState(false);

  useEffect(() => {
    const fetchRibbon = async () => {
      try {
        const res = await api.get('/awards/show_award_ribbon');
        if (res.data && res.data.show_ribbon === true) {
          setShowRibbon(true);
        }
      } catch (error) {
        console.error("Error fetching award ribbon:", error);
      }
    };
    
    // Only check ribbon if user is in an allowed role (not admin/staff/etc.)
    const currentRole = localStorage.getItem("Authorise") || "";
    if (currentRole && !["admin", "staff", "support", "accounts", "trainerspoc", "talent", "corporate", "trainer"].includes(currentRole.toLowerCase())) {
        fetchRibbon();
    }
  }, []);

  function handleSuccessMessage(message) {
    toast.success(message, {
      autoClose: 2000,
      position: "top-center",
    });
  }

  // Ticket handler
  const handleOpenTicketModal = () => {
    toast.info("You can raise a ticket here now.", {
      autoClose: 1800,
      position: "top-right",
    });
    setIsTicketModalOpen(true);
  };

  const handleProfile = (popupState) => {
    if (popupState) popupState.close();
    if (profileButtonRef.current) {
      const rect = profileButtonRef.current.getBoundingClientRect();
      setButtonRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
      });
    }
    setTimeout(() => {
      setIsModalOpen(true);
    }, 10);
  };

  const handleNotificationClick = () => {
    setNotificationCount(0);
  };

  const navigate = useNavigate();

  const handleSettings = (popupState) => {
    navigate("/settings");
    popupState.close();
  };

  const handleLogout = async () => {
    try {
      setIsLoading(true);
      const response = await AuthService.logout();
      if (response.status === 200) {
        localStorage.clear();
        clearAllSlicesThunk(dispatch);
        handleSuccessMessage("Logout successful");
        navigate("/login");
      } else {
        console.error("Logout failed with status:", response.status);
      }
      return response;
    } catch (error) {
      localStorage.clear();
      clearAllSlicesThunk(dispatch);
      console.error("Logout error:", error);
      navigate("/login");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      setIsLoadingSwitch(true);
      try {
        const response = await AuthService.roles();
        if (response && response?.status === 200) {
          const fetchedRoles = response.data.roles;
          setRoles(fetchedRoles);
          setIsMultiRole(response.data.multi_role);
          const activeRole = fetchedRoles.find((role) => role.status === true);
          if (activeRole) {
            const activeRoleNameModified = activeRole.role_name
              .toLowerCase()
              .replace(/ /g, "_");
            localStorage.setItem("Authorise", activeRoleNameModified);
            dispatch(setUserRole(activeRoleNameModified));
            setIsLoadingSwitch(false);
          }
        } else {
          console.error("Invalid response:", response);
          setIsLoadingSwitch(false);
        }
      } catch (error) {
        console.error("Error fetching data:");
        setIsLoadingSwitch(false);
      }
    }
    fetchData();
  }, [dispatch]);

  const handleRoleSwitch = async (id) => {
    setIsLoadingSwitch(true);
    navigate("/dashboard");
    try {
      const response = await AuthService.switch_role(id);
      const fetchedRoles = response?.data.roles;
      setRoles(fetchedRoles);
      setIsMultiRole(response?.data.multi_role);
      const activeRole = fetchedRoles?.find((role) => role?.status === true);
      if (activeRole) {
        const activeRoleNameModified = activeRole?.role_name
          .toLowerCase()
          .replace(/ /g, "_");
        localStorage.setItem("Authorise", activeRoleNameModified);
        dispatch(setUserRole(activeRoleNameModified));
        handleSuccessMessage(`${activeRole?.role_name} switched successfully`);
        setIsLoadingSwitch(false);
      }
    } catch (error) {
      console.error(error);
      setIsLoadingSwitch(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          boxShadow: `0px 1px 4px ${colors.grey[900]}`,
          height: showMobileHeader ? HEADER_MOBILE : HEADER_DESKTOP,
          borderRadius: "0px 0px",
          bgcolor: colors.background[100],
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          height={"100%"}
        >
          <Box display="flex" alignItems="center">
            {showIconButton && (
              <IconButton sx={{ mr: 2 }} onClick={onToggleDrawer}>
                <MenuOutlinedIcon />
              </IconButton>
            )}
            {userRole === "spoc" && <MembershipBadge />}
            {userRole === "dspoc" && <MembershipBadge />}
            {userRole === "leaders" && <MembershipBadge />}
            {userRole === "tpo" && <MembershipBadge />}
            {/* {(userRole === "spoc" ||
              userRole === "dspoc" ||
              userRole === "leaders" ||
              userRole === "tpo") && <ConnectRegistrationBadge />} */}
            {(userRole === "admin" || userRole === "business_development_manager") && <ConnectFeedbackBadge />}
            {userRole === "staff" && (
              <>
                <Searchbar />
                {shouldShowInstitutionFilters && (
                  <InstitutionFilters colors={colors} />
                )}
              </>
            )}

             {(userRole === "spoc" ||
              userRole === "dspoc" ||
              userRole === "leaders" ||
              userRole === "tpo") && <CareerTalkBadge />}
          </Box>

          {/* RIGHT ICONS AND BUTTONS */}
          <Box display="flex" alignItems="center">
            {![
              "admin",
              "staff",
              "support",
              "accounts",
              "trainerspoc",
              "talent",
              "corporate",
              "trainer",
              "desiner"
            ].includes(userRole?.toLowerCase()) && (
              <Tooltip title="View Awards">
                <Button
                  onClick={() => navigate("/multi", { state: { activeTab: "general" } })}
                  variant="contained"
                  endIcon={<EmojiEventsOutlinedIcon sx={{ zIndex: 2 }} />}
                  sx={{
                    position: "relative",
                    ml: 1,
                    mr: isMultiRole ? 0 : 1,
                    borderRadius: "8px",
                    minWidth: { xs: "40px", sm: "auto" },
                    width: { xs: "40px", sm: "auto" },
                    height: { xs: "40px", sm: "auto" },
                    padding: { xs: "0", sm: showRibbon ? "6px 16px 6px 36px" : "6px 16px" },
                    overflow: "hidden",
                    background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
                    color: "#FFFFFF",
                    fontWeight: 600,
                    textTransform: "none",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    boxShadow: "0 4px 15px rgba(255, 152, 0, 0.4)",
                    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                    animation: "pulse-glow-connect 1.5s ease-in-out infinite",
                    "& .MuiButton-endIcon": { 
                      zIndex: 2,
                      margin: { xs: showRibbon ? "0 0 0 12px" : "0", sm: "0 -4px 0 8px" }
                    },
                    
                    "@keyframes pulse-glow-connect": {
                      "0%": { boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)", transform: "scale(1)" },
                      "50%": { boxShadow: "0 6px 20px rgba(255, 152, 0, 0.6)", transform: "scale(1.06)" },
                      "100%": { boxShadow: "0 4px 12px rgba(255, 152, 0, 0.3)", transform: "scale(1)" },
                    },

                    "&:hover": {
                      background: "linear-gradient(135deg, #F57C00 0%, #E65100 100%)",
                      transform: "translateY(-2px) scale(1.08)",
                      boxShadow: "0 12px 30px rgba(255, 152, 0, 0.6)",
                      animation: "none",
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline-block" }, position: "relative", zIndex: 2 }}>Connect-2026</Box>
                  {showRibbon && (
                    <Box
                      className="winner-diagonal-banner"
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "60px",
                        height: "60px",
                        pointerEvents: "none",
                        zIndex: 10,
                      }}
                    >
                      <Box
                        sx={{
                          position: "absolute",
                          top: "8px",
                          left: "-24px",
                          background: "#0D47A1",
                          color: "#FFFFFF",
                          fontSize: "0.4rem",
                          fontWeight: 900,
                          padding: "2px 28px",
                          transform: "rotate(-45deg)",
                          boxShadow: "0 2px 6px rgba(0,0,0,0.3)",
                          letterSpacing: "0.5px",
                        }}
                      >
                        WINNER
                      </Box>
                    </Box>
                  )}
                </Button>
              </Tooltip>
            )}

            {/* Raise Ticket button for spoc only */}
            {(userRole === "spoc" || userRole === "dspoc") && (
              <Tooltip title="Raise a Ticket">
                <Button
                  onClick={handleOpenTicketModal}
                  variant="contained"
                  startIcon={<HelpOutlineIcon />}
                  sx={{
                    ml: 1,
                    borderRadius: { xs: "20px", sm: 2 },
                    minWidth: { xs: "40px", sm: "auto" },
                    width: { xs: "40px", sm: "auto" },
                    height: { xs: "40px", sm: "auto" },
                    padding: { xs: "0", sm: "6px 16px" },
                    backgroundColor: "#0288D1",
                    color: "#fff",
                    fontWeight: 600,
                    "&:hover": { backgroundColor: "#0277BD" },
                    "& .MuiButton-startIcon": { 
                      margin: { xs: 0, sm: "0 8px 0 -4px" }
                    },
                  }}
                >
                  <Box component="span" sx={{ display: { xs: "none", sm: "inline-block" } }}>Raise Ticket</Box>
                </Button>
              </Tooltip>
            )}

            {isMultiRole && (
              <PopupState variant="popover" popupId="demo-popup-menu">
                {(popupState) => (
                  <Box>
                    <Tooltip title="Roles Switch">
                      <IconButton {...bindTrigger(popupState)} sx={{ mr: 1 }}>
                        <WidgetsOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      {...bindMenu(popupState)}
                      sx={{
                        mt: 2,
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon
                          icon="fluent:window-multiple-16-filled"
                          height={15}
                          width={15}
                        />
                        <Typography variant="body2" ml={0.2}>
                          Manage User Roles
                        </Typography>
                      </Box>
                      <Divider sx={{ my: 1 }} />
                      <Box
                        sx={{
                          m: "1px 9px",
                          display: "grid",
                          gridTemplateColumns: "repeat(2, 1fr)",
                          gap: "8px",
                        }}
                      >
                        {roles.map((role) => {
                          if (role) {
                            const isRoleActive =
                              role.role_name
                                .toLowerCase()
                                .replace(/ /g, "_") === isActiveRole;

                            return (
                              <MenuItem
                                key={role.role_id}
                                disabled={isLoadingSwitch}
                                onClick={() => {
                                  if (!isRoleActive) {
                                    popupState.close();
                                    handleRoleSwitch(role.role_id);
                                  }
                                }}
                                sx={{
                                  borderRadius: 1,
                                  minWidth: 125,
                                  display: "flex",
                                  justifyContent: "center",
                                  backgroundColor: role.status
                                    ? colors.blueAccent[600]
                                    : "inherit",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    flexDirection: "column",
                                  }}
                                >
                                  {/* Render the role-specific icon here */}
                                   {role.role_name === "Admin" && (
                                    <Icon
                                      icon="ic:baseline-admin-panel-settings"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Talent" && (
                                    <Icon
                                      icon="game-icons:hive-mind"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Staff" && (
                                    <Icon
                                      icon="mdi:people-group"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Manager" && (
                                    <Icon
                                      icon="fa-solid:user-cog"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Talent Module" && (
                                    <Icon
                                      icon="fluent:brain-circuit-20-filled"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Educator" && (
                                    <Icon
                                      icon="fa-solid:user-tie"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Management" && (
                                    <Icon
                                      icon="mdi:user-group"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Spoc" && (
                                    <Icon
                                      icon="solar:user-speak-bold"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Account Manager" && (
                                    <Icon
                                      icon="mdi:account-lock-outline"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "leaders" && (
                                    <Icon
                                      icon="mdi:account-star"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "dspoc" && (
                                    <Icon
                                      icon="mdi:account-supervisor"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Corporate" && (
                                    <Icon
                                      icon="material-symbols:corporate-fare-rounded"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {role.role_name === "Business Development Manager" && (
                                    <Icon
                                      icon="mdi:briefcase-account"
                                      height={22}
                                      width={22}
                                    />
                                  )}
                                  {/* ... icons code unchanged ... */}
                                  <Typography variant="subtitle2">
                                    {role.role_name}
                                  </Typography>
                                </Box>
                              </MenuItem>
                            );
                          } else {
                            return null;
                          }
                        })}
                      </Box>
                    </Menu>
                  </Box>
                )}
              </PopupState>
            )}
            <Tooltip
              title={
                theme.palette.mode === "light"
                  ? "Toggle Dark Mode"
                  : "Toggle Light Mode"
              }
            >
              <IconButton onClick={colorMode.toggleColorMode} sx={{ mr: 1 }}>
                {theme.palette.mode === "dark" ? (
                  <DarkModeOutlinedIcon />
                ) : (
                  <LightModeOutlinedIcon />
                )}
              </IconButton>
            </Tooltip>

            <PopupState variant="popover" popupId="demo-popup-menu">
              {(popupState) => (
                <>
                  <Tooltip title="Profile">
                    <Avatar
                      alt="Remy Sharp"
                      src={Profile}
                      sx={{ width: 32, height: 32, ml: 1, cursor: "pointer" }}
                      {...bindTrigger(popupState)}
                    />
                  </Tooltip>
                  <Menu {...bindMenu(popupState)} sx={{ mt: 2.5, px: 3 }}>
                    <Box sx={{ py: 1, px: 2 }}>
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        color={colors.blueAccent[200]}
                        sx={{ ml: 0.4, mt: -0.8 }}
                      >
                        {formattedUserName ? formattedUserName : "User Name"}
                      </Typography>
                      <Badge
                        color="secondary"
                        badgeContent="active"
                        variant="dot"
                      >
                        <Typography
                          fontWeight="bold"
                          variant="caption"
                          sx={{
                            bgcolor: colors.grey[900],
                            px: 0.8,
                            borderRadius: 1,
                          }}
                        >
                          {isActiveRole ? isActiveRole : "Email Address"}
                        </Typography>
                      </Badge>
                    </Box>
                    <Divider />
                    <MenuItem
                      onClick={() => {
                        const menuItem = document.activeElement;
                        if (menuItem) {
                          const rect = menuItem.getBoundingClientRect();
                          setButtonRect({
                            top: rect.top,
                            left: rect.left,
                            width: rect.width,
                            height: rect.height,
                            centerX: rect.left + rect.width / 2,
                            centerY: rect.top + rect.height / 2,
                          });
                          setTimeout(() => {
                            setIsModalOpen(true);
                            popupState.close();
                          }, 10);
                        } else {
                          handleProfile(popupState);
                        }
                      }}
                      disabled={true}
                      sx={{
                        borderRadius: 1,
                        minWidth: 125,
                        m: "2px 10px",
                        mt: 1,
                      }}
                    >
                      <AccountCircleOutlinedIcon sx={{ mr: 1 }} />
                      Institute Profile
                    </MenuItem>

                    <MenuItem
                      onClick={() => handleSettings(popupState)}
                      sx={{
                        borderRadius: 1,
                        minWidth: 125,
                        m: "2px 10px",
                      }}
                      disabled
                    >
                      <SettingsOutlinedIcon sx={{ mr: 1 }} />
                      Settings
                    </MenuItem>
                    <Divider />
                    <MenuItem
                      disabled={isLoading}
                      onClick={handleLogout}
                      sx={{
                        borderRadius: 1,
                        minWidth: 125,
                        m: "3px 10px",
                        "&:hover": {
                          backgroundColor: colors.redAccent[800],
                        },
                      }}
                    >
                      <LogoutOutlinedIcon sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              )}
            </PopupState>
          </Box>
        </Box>
      </Box>

      {/* Animated Modal */}
      <AnimatedModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        buttonRect={buttonRect}
      />
      {/* Raise Ticket Modal */}
      <TicketModal open={isTicketModalOpen} onClose={() => setIsTicketModalOpen(false)} />
      
      {/* Award Winner Popup */}
      <AwardWinnerPopup />
      
      {/* Voting Button & Global Popups */}
      <VotingButton popupOnly={true} />
    </>
  );
};

export default Topbar;
